import { createClient } from "@supabase/supabase-js";

// iter 73.9 — Two-layer defence against third-party fetch interception.
//
// Layer 1: unique `storageKey` — see below. Stops other Supabase
// clients from racing on the same localStorage session token.
//
// Layer 2: a *private* fetch wrapper — critical.
// The Emergent preview overlay (`assets.emergent.sh/scripts/emergent-main.js`)
// monkey-patches `window.fetch` and reads the response body as part of
// its instrumentation. That drains the stream before Supabase's GoTrue
// client can call `response.json()` on it, tripping the native
// "Failed to execute 'json' on 'Response': body stream already read"
// error on the very first login attempt in a real browser session.
//
// The fix is to capture the ORIGINAL `window.fetch` at module load
// (before the overlay's script has a chance to patch it) and hand
// Supabase its own copy via `global.fetch`. Once Supabase is bound
// to the unwrapped fetch, no downstream monkey-patch can double-read
// our responses.
//
// If the overlay has *already* patched fetch by the time this module
// loads (rare but possible depending on script order), we fall back to
// a wrapper that immediately clones every Response and returns the
// clone — same net effect, one extra allocation per request.
const _rawFetch = (typeof window !== "undefined" && window.fetch)
  ? window.fetch.bind(window)
  : undefined;

const safeFetch = _rawFetch
  ? (...args) => _rawFetch(...args).then((r) => r.clone())
  : undefined;

export const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      // Unique storage key so our GoTrueClient never collides with any
      // other Supabase client sharing the same browser context (see
      // above comment for the collision that motivated this).
      storageKey: "logbook-legal-auth",
    },
    // Layer 2 — private fetch. Ignored if `safeFetch` is undefined
    // (SSR / test environments); Supabase falls back to global fetch.
    ...(safeFetch ? { global: { fetch: safeFetch } } : {}),
  }
);

// iter 73.9 — One-time cleanup of legacy default-key session tokens.
// If a user signed in BEFORE we introduced `storageKey`, their session
// is stashed under the default key `sb-<project-ref>-auth-token`. That
// entry is now orphaned but the Emergent overlay may still read it and
// trigger re-auth races on page load. Purge it once on module load so
// the next login is 100% clean. Safe to do unconditionally — losing an
// orphaned token forces one extra password entry, nothing more.
try {
  if (typeof window !== "undefined" && window.localStorage) {
    for (const k of Object.keys(window.localStorage)) {
      if (k.startsWith("sb-") && k.endsWith("-auth-token") && k !== "logbook-legal-auth") {
        window.localStorage.removeItem(k);
      }
    }
  }
} catch { /* private-mode / quota — ignore */ }
